import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { UserInfoStateRecoil } from '../utilities/globaleState';
import { isObjectEmpty } from '../utilities/helpers';
import API from '../utilities/API';

interface UseAuthGuardOptions {
  redirectTo?: string;
  requireAuth?: boolean;
}

interface UseAuthGuardReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  checkAuth: () => Promise<void>;
}

export const useAuthGuard = (options: UseAuthGuardOptions = {}): UseAuthGuardReturn => {
  const { redirectTo = '/', requireAuth = true } = options;

  const navigate = useNavigate();
  const [userInfoState, setUserInfoState] = useRecoilState(UserInfoStateRecoil);
  // Utiliser un état local pour le chargement au lieu de l'état global
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const api = new API();

  const checkAuth = async (): Promise<void> => {
    // Éviter les vérifications multiples simultanées
    if (isChecking || isAuthChecked) return;

    try {
      setIsChecking(true);
      setIsLoading(true);

      // Vérifier si on a un token dans le localStorage
      const token = localStorage.getItem("token");

      if (!token) {
        if (requireAuth) {
          // Sauvegarder la page actuelle avant la redirection
          const currentPath = window.location.pathname;
          if (currentPath !== redirectTo) {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
          }
          navigate(redirectTo);
        }
        setIsAuthChecked(true);
        return;
      }

      // Si on a un token mais pas d'info utilisateur, vérifier auprès du serveur
      if (isObjectEmpty(userInfoState)) {
        const response = await api.apiData("get", "user/currentuser");

        if (response?.status === 200 && response?.user) {
          setUserInfoState(response.user);
        } else {
          // Token invalide, nettoyer et rediriger
          localStorage.removeItem("token");
          setUserInfoState({});
          if (requireAuth) {
            navigate(redirectTo);
          }
        }
      }

      setIsAuthChecked(true);
    } catch (error) {
      console.error('Erreur lors de la vérification d\'authentification:', error);
      localStorage.removeItem("token");
      setUserInfoState({});
      if (requireAuth) {
        navigate(redirectTo);
      }
      setIsAuthChecked(true);
    } finally {
      setIsLoading(false);
      setIsChecking(false);
    }
  };

  useEffect(() => {
    // Ne vérifier l'auth que si ce n'est pas déjà fait
    if (!isAuthChecked && !isChecking) {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dépendances vides pour n'exécuter qu'une fois

  const isAuthenticated = !isObjectEmpty(userInfoState) && !!localStorage.getItem("token");

  return {
    isAuthenticated,
    isLoading,
    user: userInfoState,
    checkAuth
  };
};